import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Text,
  useToast,
  SimpleGrid,
  Avatar,
  Image,
  Link,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Select
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { cqAPI, HOST } from "../../lib/Auth";
import { useNavigate, useLocation } from 'react-router-dom';
import moment from "moment";
import {WORKER_CLOUD9, WORKER_DENTRIXASCEND} from "../AdapterSetting/constants";
import { useAppContext } from "../../AppContext";

function Appointments() {
  const [appointments, setAppointments] = useState([]);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [selectedLocationBid, setSelectedLocationBid] = useState('');
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { locations, isPatient } = useAppContext();
  const bookableLocations = (locations || []).filter(loc => loc.bookable !== false);
  const isMultiOffice = isPatient && bookableLocations.length > 1;
  const sortedLocations = [...bookableLocations].sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const fetchAppointments = async () => {
    try {
      const response = await cqAPI.get("/pp/appointments");
      if (response.type === "ok") {
        setAppointments(response.data);
      } else {
        toast({
          title: "Error fetching appointments",
          description: response.msg,
          status: "error",
        });
      }
    } catch (error) {
      console.error("Error fetching appointments:", error);
      toast({
        title: "Error fetching appointments",
        description: error.message,
        status: "error",
      });
    }
  };

  const confirmAppointment = async (appointmentId) => {
    try {
      const response = await cqAPI.get(`/appconfirm/${appointmentId}`);
      if (response.type === "ok") {
        toast({
          title: "Appointment confirmed successfully",
          status: "success",
        });
        fetchAppointments(); // Refresh the appointments list
      } else {
        toast({
          title: "Error confirming appointment",
          description: response.msg,
          status: "error",
        });
      }
    } catch (error) {
      console.error("Error confirming appointment:", error);
      toast({
        title: "Error confirming appointment",
        description: error.message,
        status: "error",
      });
    }
  };
  
  const modifyAppointment = async (appointmentId, duration, provider_id, worker_type, service_ids) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('provider_id',provider_id);
    searchParams.set('app_id',appointmentId.split(':')[1]);
    let service_id;
    switch (worker_type) {
      case WORKER_CLOUD9:
        if (service_ids && service_ids.length) {
          service_id = service_ids[0];
        }
        break;
      case WORKER_DENTRIXASCEND:
        if (service_ids?.length) {
          service_id = service_ids[0]?.split(':')[0];
          navigate({
            pathname: `/appointments/new/service/${service_id}`,
            search: `?${searchParams.toString()}`
          });
        } else {
          navigate({
            pathname: `/appointments/new`,
            search: `?${searchParams.toString()}`
          });
        }
        return;
      default:
        service_id = 1;
    }
    
    navigate({
      pathname: `/appointments/new/service/${service_id}:${Math.ceil(duration/15)}`,
      search: `?${searchParams.toString()}`
    });
  };
  
  const cancelAppointment = async (appointmentId) => {
    try {
      const response = await cqAPI.del(`/appointment/${appointmentId.split(':')[1]}?author_entity=pp`,{
        
      });
      if (response.type === "ok") {
        toast({
          title: response.msg,
          status: "success",
        });
        fetchAppointments(); // Refresh the appointments list
      } else {
        toast({
          title: "Error cancelling appointment",
          description: response.msg,
          status: "error",
        });
      }
    } catch (error) {
      console.error("Error cancelling appointment:", error);
      toast({
        title: "Error confirming appointment",
        description: error.message,
        status: "error",
      });
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, []);

  const getAppointmentDuration = (duration, start, end) => {
    return duration ?? Math.floor((new Date(end) - new Date(start)) / (1000 * 60))
  }

  return (
    <Box
      p={4}
      overflowY="auto"
      maxHeight="100vh"
      width="100%"
      position="relative"
      id="new-appointment-page"
    >
      {/* Location selector modal for multi-office patients */}
      <Modal isOpen={showLocationModal} onClose={() => setShowLocationModal(false)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Select Office</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text pb={2}> Which office would you like to schedule an appointment at</Text>
            <Select
              placeholder="Select a location"
              value={selectedLocationBid}
              onChange={(e) => setSelectedLocationBid(e.target.value)}
            >
              {sortedLocations.map((loc) => (
                <option key={loc.bid} value={loc.bid}>
                  {loc.groupname || loc.name}
                </option>
              ))}
            </Select>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setShowLocationModal(false)}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              isDisabled={!selectedLocationBid}
              onClick={() => {
                setShowLocationModal(false);
                const searchParams = new URLSearchParams(location.search);
                searchParams.set("bid", selectedLocationBid);
                navigate({ pathname: '/appointments/new', search: searchParams.toString() });
              }}
            >
              Next
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Box textAlign="right" mt={4} paddingBottom="25px">
        <Button
          isDisabled={bookableLocations.length === 0}
          onClick={() => {
            if (isMultiOffice) {
              setShowLocationModal(true);
            } else if (bookableLocations.length === 1) {
              // Single connected location — book there directly, even if the patient is currently
              // viewing a role-only sibling (otherwise the booking would target the unlinked bid).
              const searchParams = new URLSearchParams(location.search);
              searchParams.set("bid", String(bookableLocations[0].bid));
              navigate({ pathname: '/appointments/new', search: searchParams.toString() });
            }
          }}
        >
          New Appointment
        </Button>
      </Box>
      <SimpleGrid columns={{ base: 1, md: 2, lg: 1 }} id="appointment-list">
        {Array.isArray(appointments) &&
          appointments.map(
            ({
              _id,
              address,
              business,
              confirmed,
              starttime,
              endtime,
              duration,
              provider_name,
              provider_id,
              pid,
              worker_type,
              service_ids
            }) => (
              <Box
                key={_id}
                borderWidth="1px"
                borderRadius="lg"
                mb={4}
                width="100%"
                className="actionableItem"
              >
                <Flex
                  direction={{ base: "column", md: "row" }}
                  p={4}
                  justify="space-between"
                  align={{ base: "stretch", md: "center" }}
                  height="100%"
                >
                  {/* Block 1: Provider Picture and Name */}
                  <Flex
                    direction="column"
                    align="center"
                    minWidth="80px"
                    mb={{ base: 4, md: 0 }}
                  >
                    {pid ? (
                      <Image
                        borderRadius="full"
                        width="80px"
                        height="80px"
                        src={`${HOST}/profileimage?uid=${pid}`}
                      />
                    ) : (
                      <Avatar
                        name={provider_name}
                        size="lg"
                        width="80px"
                        height="80px"
                      />
                    )}
                    <Text fontSize="sm" mt={2}>
                      <b>{provider_name}</b>
                    </Text>
                  </Flex>

                  {/* Block 2: Business Information */}
                  <Box
                    textAlign="left"
                    ml={{ base: 0, md: 4 }}
                    mb={{ base: 4, md: 0 }}
                    minWidth={{ base: "100%", md: "200px" }}
                  >
                    <Text fontSize="sm">
                      <b>{business}</b>
                    </Text>
                    <Text fontSize="sm">
                      {address.line1}
                      <br />
                      {address.line2}
                      {address.line2 && <br />}
                      {address.city}
                      <br />
                      {address.zip}
                    </Text>
                  </Box>

                  {/* Block 3: Appointment Time and Duration */}
                  <Box
                    textAlign="left"
                    ml={{ base: 0, md: 4 }}
                    mb={{ base: 4, md: 0 }}
                    minWidth={{ base: "100%", md: "200px" }}
                    id="appointment-datetime"
                  >
                    <Text fontSize="sm">
                      {moment(starttime).format("MMMM Do YYYY, h:mm:ss a")}
                    </Text>
                    <Text fontSize="sm"> {getAppointmentDuration(duration, starttime, endtime)} minutes</Text>
                  </Box>

                    <Box textAlign="center" minWidth="120px">
                      {confirmed ? (
                        <Text fontSize="sm" color="green.500">
                          <b>Confirmed</b>
                        </Text>
                      ) : _id.includes(":") ? (
                        <Button
                          colorScheme="blue"
                          leftIcon={<FontAwesomeIcon icon={faCheck} />}
                          onClick={() => confirmAppointment(_id)}
                          id="confirm-appointment-btn"
                        >
                          Confirm
                        </Button>
                      ) : (
                        <Text fontSize="sm" color="green.500">
                          <b>Awaiting Office Confirmation</b>
                        </Text>
                      )}

                      {_id.includes(':') && (new Date(starttime) > new Date()) && (
                        <Box display="flex" flexDirection="column" alignItems="center" mt={2}>
                          <Link 
                            onClick={() => modifyAppointment(_id, duration, provider_id, worker_type, service_ids)} 
                            textDecoration="underline"
                            mb={2}
                          >
                            Modify
                          </Link>
                          <Link 
                            display="none"
                            onClick={() => cancelAppointment(_id)} 
                            textDecoration="underline"
                          >
                            Cancel
                          </Link>
                        </Box>
                      )}
                    </Box>
                  
                </Flex>
              </Box>
            )
          )}
      </SimpleGrid>
    </Box>
  );
}

export default Appointments;
